import { DocumentTextIcon } from '@interstate/components/Icons/DocumentTextIcon';
import type { ContractingStatusInfo, ContractStatusRecord } from '@makemydeal/dr-dash-types';
import { ContractStatusBadgeInfo } from '@makemydeal/dr-dash-types';
import type { FC } from 'react';
import { ContractingCustomStyledBadge } from './ContractingCustomStyledBadge';
import { useDispatch } from 'react-redux';
import type { ContractCodes } from '@makemydeal/dr-shared-types';
import * as navigationActionCreators from '@makemydeal/dr-dash-store/actions/navigationActionCreators';

type ContractStatusBadgeProps = {
    contractingStatusInfo: ContractingStatusInfo;
};

type BadgeActionHandlers = Partial<Record<Exclude<ContractStatusRecord['action'], 'none'>, () => void>>;

const DEFAULT_BADGE_INFO: ContractStatusRecord = {
    label: 'Unknown Status',
    variant: 'transparent',
    action: 'none'
};

export const ContractStatusBadge: FC<ContractStatusBadgeProps> = ({ contractingStatusInfo }) => {
    const dispatch = useDispatch();
    const contractStatusCode = contractingStatusInfo.contractStatusCode;
    const badgeInfo = getStatusCodeToBadgeInfo(contractStatusCode);

    const handleBadgeClick = () => {
        executeBadgeAction(badgeInfo, {
            navigateToDDJ: () => {
                dispatch(navigationActionCreators.navigateToDDJ());
            }
        });
    };

    const isClickable = badgeInfo.action !== 'none';

    return (
        <ContractingCustomStyledBadge
            data-testid="contract-status-badge"
            variant={badgeInfo.variant}
            startIcon={<DocumentTextIcon />}
            $isClickable={isClickable}
        >
            <span data-testid="contract-status-badge-text" onClick={handleBadgeClick} tabIndex={isClickable ? 0 : -1}>
                {badgeInfo.label}
            </span>
        </ContractingCustomStyledBadge>
    );
};

export const executeBadgeAction = (badgeInfo?: ContractStatusRecord, handlers: BadgeActionHandlers = {}): void => {
    if (!badgeInfo || badgeInfo.action === 'none') {
        return;
    }

    switch (badgeInfo.action) {
        case 'navigateToDDJ':
            if (handlers.navigateToDDJ) {
                handlers.navigateToDDJ();
            }
            return;
        case 'showErrors':
            if (handlers.showErrors) {
                handlers.showErrors();
            }
            return;
        default:
            return;
    }
};

export const getStatusCodeToBadgeInfo = (code: ContractCodes | undefined): ContractStatusRecord => {
    if (code && code in ContractStatusBadgeInfo) {
        return ContractStatusBadgeInfo[code];
    }

    return DEFAULT_BADGE_INFO;
};
